import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CARGO_COMPATIBILITY, type ChemVessel, type ChemTank } from "@/data/chemCarriageData";
import {
  PLAN_TANKS, PLAN_W, PLAN_H, HULL_PATH, INNER_HULL_PATH, FRAME_MARKS,
  GROUP_FILL, GROUP_LABEL, type TankShape, type TankGroup,
} from "@/data/tankPlanLayout";
import { useTankLevels } from "@/data/tankLevelsStore";
import { CheckCircle2, AlertTriangle, FlaskConical, Download } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// === Status overlay for cargo tanks (from chemCarriageData) ===
const STATUS_RING: Record<string, string> = {
  "Clean":             "hsl(160 70% 38%)",
  "Last-Cargo":        "hsl(38 92% 45%)",
  "Slop":              "hsl(20 90% 48%)",
  "Under Maintenance": "hsl(0 75% 50%)",
};

function checkCompat(prior: string, proposed: string) {
  const e = CARGO_COMPATIBILITY[prior];
  if (!e) return { ok: true as const, reason: "No restriction on file" };
  if (e.incompatible.some(i => i.toLowerCase() === proposed.toLowerCase()))
    return { ok: false as const, reason: `Incompatible with last cargo (${prior})` };
  if (e.compatible.some(i => i.toLowerCase() === proposed.toLowerCase()))
    return { ok: true as const, reason: `Compatible with last cargo (${prior})` };
  return { ok: true as const, reason: "Wall-wash recommended" };
}

// Map data-driven cargo tanks (e.g. "1P", "1S", "1C") to plan-block ids
function mapCargoTanks(vessel: ChemVessel): Map<string, ChemTank> {
  const m = new Map<string, ChemTank>();
  for (const t of vessel.tanks) m.set(t.no, t);
  return m;
}

type HoverInfo = { id: string; label: string; group: TankGroup; pct: number; cap?: number; unit?: string; sub?: string };

export const InteractiveTankPlan = ({
  vessel,
  proposedCargo,
  compact = false,
}: {
  vessel: ChemVessel;
  proposedCargo?: string;
  compact?: boolean;
}) => {
  const levels = useTankLevels(vessel.name);
  const [open, setOpen] = useState<ChemTank | null>(null);
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const [cargoInput, setCargoInput] = useState(proposedCargo ?? "");

  const cargoMap = useMemo(() => mapCargoTanks(vessel), [vessel]);

  const renderTank = (t: TankShape) => {
    const baseFill = GROUP_FILL[t.group];
    const cargo = t.group === "cargo" ? cargoMap.get(t.id) : undefined;
    const ring = cargo ? STATUS_RING[cargo.status] ?? STATUS_RING["Clean"] : "hsl(215 25% 30%)";
    const pct = t.fillable ? (levels[t.id] ?? 0) : 0;
    const isCircle = t.shape === "circle";

    // Geometry
    const cx = isCircle ? t.cx! : t.x! + t.w! / 2;
    const cy = isCircle ? t.cy! : t.y! + t.h! / 2;
    const labelY = cy + 3;

    const compat = cargo && cargoInput ? checkCompat(cargo.l3c[0], cargoInput) : null;

    const onEnter = () => setHover({
      id: t.id, label: t.label, group: t.group, pct,
      cap: t.capacity, unit: t.unit,
      sub: cargo ? `L3C: ${cargo.l3c.join(" → ")}` : t.sub,
    });

    return (
      <g
        key={t.id}
        style={{ cursor: cargo || t.fillable ? "pointer" : "default" }}
        onClick={() => { if (cargo) setOpen(cargo); }}
        onMouseEnter={onEnter}
        onMouseLeave={() => setHover(null)}
      >
        {isCircle ? (
          <>
            <circle
              cx={cx} cy={cy} r={t.r!}
              fill="hsl(210 25% 97%)"
              stroke={ring}
              strokeWidth={cargo ? 1.8 : 1}
            />
            {/* liquid fill via clipped rect */}
            {t.fillable && pct > 0 && (
              <>
                <defs>
                  <clipPath id={`clip-${t.id}`}>
                    <circle cx={cx} cy={cy} r={t.r! - 1.2} />
                  </clipPath>
                </defs>
                <rect
                  x={cx - t.r!} y={cy + t.r! - (2 * t.r! * pct) / 100}
                  width={2 * t.r!} height={(2 * t.r! * pct) / 100}
                  fill={baseFill} fillOpacity={0.55}
                  clipPath={`url(#clip-${t.id})`}
                />
              </>
            )}
            {/* inner ring (cylindrical look) */}
            <circle cx={cx} cy={cy} r={t.r! - 4} fill="none"
              stroke="hsl(215 25% 30%)" strokeOpacity={0.25} strokeWidth={0.6} />
            {/* X marker like the GA drawing */}
            <line x1={cx - t.r! + 6} y1={cy - t.r! + 6} x2={cx + t.r! - 6} y2={cy + t.r! - 6}
              stroke="hsl(215 25% 30%)" strokeOpacity={0.18} strokeWidth={0.6} />
            <line x1={cx - t.r! + 6} y1={cy + t.r! - 6} x2={cx + t.r! - 6} y2={cy - t.r! + 6}
              stroke="hsl(215 25% 30%)" strokeOpacity={0.18} strokeWidth={0.6} />
          </>
        ) : (
          <>
            <rect
              x={t.x} y={t.y} width={t.w} height={t.h}
              rx={t.rounded ?? 2} ry={t.rounded ?? 2}
              fill="hsl(210 25% 97%)"
              stroke={ring}
              strokeWidth={1}
            />
            {t.fillable && pct > 0 && (
              <rect
                x={t.x} y={t.y! + t.h! - (t.h! * pct) / 100}
                width={t.w} height={(t.h! * pct) / 100}
                rx={t.rounded ?? 2} ry={t.rounded ?? 2}
                fill={baseFill} fillOpacity={0.55}
              />
            )}
          </>
        )}

        {/* primary label */}
        <text
          x={cx} y={labelY}
          textAnchor="middle"
          fontSize={isCircle ? 13 : Math.min(10, (t.h ?? 20) / 2.4)}
          fontWeight={700} fill="hsl(215 35% 18%)"
          style={{ pointerEvents: "none" }}
        >
          {t.label}
        </text>
        {/* % indicator */}
        {t.fillable && (isCircle || (t.h ?? 0) >= 26) && (
          <text
            x={cx} y={isCircle ? cy + t.r! - 6 : t.y! + t.h! - 3}
            textAnchor="middle" fontSize={isCircle ? 9 : 8}
            fill="hsl(215 35% 18%)" fillOpacity={0.7}
            style={{ pointerEvents: "none" }}
          >
            {pct}%
          </text>
        )}
        {/* sub label for non-cargo rectangles */}
        {!isCircle && t.sub && (t.h ?? 0) >= 36 && (
          <text x={cx} y={cy - 8} textAnchor="middle" fontSize={6.5}
            fill="hsl(215 25% 25%)" fillOpacity={0.7} style={{ pointerEvents: "none" }}>
            {t.sub}
          </text>
        )}
        {/* compat badge */}
        {compat && isCircle && (
          <g transform={`translate(${cx + t.r! - 8}, ${cy - t.r! + 8})`} style={{ pointerEvents: "none" }}>
            <circle r={7} fill={compat.ok ? "hsl(160 70% 38%)" : "hsl(0 75% 50%)"} />
            <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
              fontSize={9} fontWeight={700} fill="white">{compat.ok ? "✓" : "!"}</text>
          </g>
        )}
      </g>
    );
  };

  return (
    <Card>
      <CardContent className={cn("p-4", compact && "p-3")}>
        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
          <div>
            <p className="font-display font-bold text-sm">
              {vessel.name} — General Arrangement (Plan View · Tank Top)
            </p>
            <p className="text-[11px] text-muted-foreground">
              {vessel.type} · {vessel.coatingType} · {vessel.tankCount} cargo tanks · {vessel.capacityM3} m³ ·
              <span className="ml-1">Live tank levels (admin-editable)</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={cargoInput}
              onChange={e => setCargoInput(e.target.value)}
              placeholder="Test compatibility (e.g. Methanol)"
              className="h-8 text-xs w-56"
            />
          </div>
        </div>

        <div className="rounded-xl border border-border/60 bg-white p-2 overflow-x-auto">
          <svg viewBox={`0 0 ${PLAN_W} ${PLAN_H}`} className="w-full h-auto" style={{ minWidth: 960 }}>
            {/* Outer hull */}
            <path d={HULL_PATH} fill="hsl(210 30% 97%)" stroke="hsl(215 25% 22%)" strokeWidth={2.2} />
            {/* Inner hull (parallel mid-body) */}
            <path d={INNER_HULL_PATH} fill="none" stroke="hsl(215 25% 30%)" strokeOpacity={0.45} strokeWidth={1} />
            {/* Centerline */}
            <line x1={30} y1={240} x2={1360} y2={240}
              stroke="hsl(215 25% 30%)" strokeOpacity={0.35} strokeWidth={0.6} strokeDasharray="4 3" />

            {/* Frame number scale */}
            {FRAME_MARKS.map((f, i) => {
              const x = 60 + (i / (FRAME_MARKS.length - 1)) * 1230;
              return (
                <g key={f}>
                  <line x1={x} y1={445} x2={x} y2={451} stroke="hsl(215 20% 50%)" strokeWidth={1} />
                  <text x={x} y={462} textAnchor="middle" fontSize={8} fill="hsl(215 20% 40%)">{f}</text>
                </g>
              );
            })}

            {/* Orientation labels */}
            <text x={30}  y={28} fontSize={10} fontWeight={700} fill="hsl(215 25% 30%)">STERN ◀</text>
            <text x={1370} y={28} fontSize={10} fontWeight={700} fill="hsl(215 25% 30%)" textAnchor="end">▶ BOW</text>
            <text x={700} y={28} fontSize={10} fontWeight={700} fill="hsl(215 25% 30%)" textAnchor="middle">PORT (P) ▲   STBD (S) ▼ — Deck 2 / Tween Deck</text>

            {/* All tanks */}
            {PLAN_TANKS.map(renderTank)}

            {/* Hover tooltip */}
            {hover && (
              <g transform={`translate(${PLAN_W - 245}, ${PLAN_H - 78})`}>
                <rect width={235} height={68} rx={5} fill="hsl(215 30% 15% / 0.94)" />
                <text x={9} y={15} fill="white" fontSize={10} fontWeight={700}>
                  {hover.label} · {GROUP_LABEL[hover.group]}
                </text>
                <text x={9} y={30} fill="hsl(210 20% 80%)" fontSize={9}>
                  Level: {hover.pct}% {hover.cap ? `· Cap ${hover.cap} ${hover.unit}` : ""}
                </text>
                {hover.sub && (
                  <text x={9} y={44} fill="hsl(210 20% 80%)" fontSize={9}>{hover.sub}</text>
                )}
                <text x={9} y={60} fill="hsl(170 60% 65%)" fontSize={8.5} fontStyle="italic">
                  {hover.sub ? "Click for L3C history & compatibility" : "Read-only area"}
                </text>
              </g>
            )}
          </svg>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[10px] text-muted-foreground">
          {(["cargo","ballast","fuel","service","slop","void","machinery"] as TankGroup[]).map(g => (
            <span key={g} className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm" style={{ background: GROUP_FILL[g], opacity: 0.55 }} />
              {GROUP_LABEL[g]}
            </span>
          ))}
          <span className="ml-auto italic">Hover any tank for live level · click cargo tanks for L3C & compatibility</span>
        </div>
      </CardContent>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-lg">
          {open && (() => {
            const compat = cargoInput ? checkCompat(open.l3c[0], cargoInput) : null;
            const pct = levels[open.no] ?? 0;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FlaskConical className="w-4 h-4 text-teal" />
                    {vessel.name} · Tank {open.no}
                  </DialogTitle>
                  <DialogDescription>
                    {open.capacity} m³ · {open.coating} · Live level: <strong>{pct}%</strong> · Status:{" "}
                    <Badge variant="outline" className="ml-1 text-[10px]">{open.status}</Badge>
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Last 3 Cargoes (L3C)</p>
                    <div className="grid grid-cols-3 gap-2">
                      {open.l3c.map((c, i) => (
                        <div key={i} className="rounded-md bg-muted/60 p-2 text-center">
                          <p className="text-[9px] uppercase text-muted-foreground">{i === 0 ? "Last" : i === 1 ? "Prior" : "3rd back"}</p>
                          <p className="font-medium text-xs mt-0.5">{c}</p>
                          <p className="text-[9px] text-emerald-600 mt-0.5">Wall-wash: pass</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {compat && (
                    <div className={cn(
                      "rounded-lg border p-3 text-xs",
                      compat.ok
                        ? "border-emerald-500/40 bg-emerald-500/10"
                        : "border-rose-500/40 bg-rose-500/10"
                    )}>
                      <div className="flex items-center gap-2 font-bold">
                        {compat.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-rose-600" />}
                        Compatibility for "{cargoInput}"
                      </div>
                      <p className="mt-1 text-muted-foreground">{compat.reason}</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="text-xs"
                      onClick={() => toast.success("Generating: L3C Declaration PDF", { description: `${vessel.name} · Tank ${open.no}` })}>
                      <Download className="w-3 h-3 mr-1" />L3C Declaration
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs"
                      onClick={() => toast.success("Wall-wash certificates queued", { description: `Tank ${open.no} · Saybolt` })}>
                      Wall-wash certs
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InteractiveTankPlan;
